import { css } from '@emotion/react'
import InfoIcon from '@material-ui/icons/Info'
import * as Yup from 'yup'
import { ErrorMessageBox } from '@/components/ErrorMessageBox'
import {
  HakajimaiPrefecture,
  hakajimaiPrefectureSchema
} from '@/components/HakajimaiPrefecture/HakajimaiPrefecture'
import { Heading } from '@/components/Heading'
import { JPXAndSSL } from '@/components/JPXAndSSL'
import { RequestForm } from '@/components/RequestForm'
import { TwoStepsStyled } from '@/components/Steps'
import { SubmitButtonWithAgreement } from '@/components/SubmitButtonWithAgreement'
import { UserAddress, userAddressSchema } from '@/components/UserAddress'
import { UserEmail, userEmailSchema } from '@/components/UserEmail'
import { UserName, userNameSchema } from '@/components/UserName'
import { UserTel, userTelSchema } from '@/components/UserTel'
import { MailMagazine, mailMagazineSchema } from '@/components/MailMagazine'
import { Crm, Purpose, hakajimaiRequestServiceId } from '@/constants/CrmClasses'
import { TrackingCategory } from '@/constants/TrackingCategories'
import { useCvTracking } from '@/hooks/use-cv-tracking'
import { AddMarginWrapper } from '@lifedot/components/AddMarginWrapper'
import { mq } from '@lifedot/styles/mediaQuery'
import { palette } from '@lifedot/styles/palette'
import { typography } from '@lifedot/styles/typography'
import { ga4EventPush } from '@lifedot/tracking'
import { HakajimaiRequestProps } from '@/runtimes/ohaka/hakajimai_requests/new/new'
import { dataConvertFunction } from './dependencies'
import { ReCAPTCHA } from '@/components/ReCAPTCHA'

const styles = {
  caption: css({
    color: palette.main03,
    marginTop: 4
  }),
  content: css({
    marginTop: 20,
    [mq('sp')]: {
      padding: '0 12px'
    }
  }),
  form: css({
    marginTop: 8,
    [mq('sp')]: {
      margin: '8px -12px 0'
    },
    'dl + dl': {
      borderTop: 'none'
    }
  }),
  icon: css({
    verticalAlign: 'text-top'
  })
}

const hakajimaiRequestSchema = {
  requests: Yup.array().of(
    Yup.object({
      type: Yup.string().required().default(Crm.hakajimaiRequest),
      service_id: Yup.string().required().default(hakajimaiRequestServiceId),
      purpose: Yup.string().required().default(Purpose.brochure)
    })
  )
}

const schema = Yup.object({
  ...userNameSchema,
  ...userEmailSchema,
  ...userTelSchema,
  ...userAddressSchema,
  ...mailMagazineSchema,
  ...hakajimaiPrefectureSchema,
  ...hakajimaiRequestSchema
})

export type FormSchemaType = Yup.InferType<typeof schema>

type ResponseDataType = {
  code: string
}

export const HakajimaiRequest = ({ guidebook }: HakajimaiRequestProps) => {
  const defaultValue = {
    requests: [
      {
        type: Crm.hakajimaiRequest,
        service_id: hakajimaiRequestServiceId,
        purpose: Purpose.brochure
      }
    ]
  }
  const cvTracking = useCvTracking({
    category: TrackingCategory.CV_COMPLETE,
    label: '墓じまい'
  })
  const onSuccess = ({ code }: ResponseDataType) => {
    cvTracking()
    ga4EventPush({
      event: 'purchase',
      type: '墓じまい',
      transactionId: code
    })
  }

  return (
    <RequestForm<FormSchemaType, ResponseDataType>
      dataConvertFunction={dataConvertFunction}
      defaultValues={defaultValue}
      onSuccess={onSuccess}
      schema={schema}
    >
      <div css={styles.content}>
        <AddMarginWrapper spacing={8}>
          <AddMarginWrapper spacing={6}>
            <TwoStepsStyled currentStep={1} />
            <div>
              <h1 css={typography.headingS}>
                {guidebook
                  ? '墓じまいガイドブックをもらう（無料）'
                  : '墓じまいの見積もり・提案をもらう（無料）'}
              </h1>
              <p>
                {guidebook
                  ? '資料送付のためにお客様情報の入力をお願い致します。'
                  : 'お電話にて詳細をヒアリングさせていただくため、お客様情報のご記入をお願いいたします。'}
              </p>
            </div>
            <section>
              <Heading text="墓じまい情報の入力" />
              <div css={styles.form}>
                <HakajimaiPrefecture />
              </div>
            </section>
            <section>
              <Heading text="お客様情報の入力" />
              <p css={[typography.textS, styles.caption]}>
                <InfoIcon style={{ fontSize: 14 }} css={styles.icon} />
                お客様の情報が一般に公開されることは一切ありませんのでご安心ください
              </p>
              <div css={styles.form}>
                <UserName />
                <UserEmail />
                <UserTel />
                <UserAddress />
                <MailMagazine />
              </div>
              <ReCAPTCHA />
            </section>
          </AddMarginWrapper>
          <ErrorMessageBox />
          <SubmitButtonWithAgreement />
          <JPXAndSSL />
        </AddMarginWrapper>
      </div>
    </RequestForm>
  )
}
